import React from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { DaInput, DaSelect, InternalGrid, Box } from '@paygreen/paygreen-ui';
import { FormControlWrapper } from '../../../components';
import { IsMobile } from '../../../utils/tools';
import { countryOptions } from '../../../utils/constants';
import { billingAddressPatterns } from '../shared';

const UserCompanyBillingAddressForm = ({
    t,
    children,
    defaultValues,
    onSubmit,
}) => {
    const { register, errors, setValue } = useFormContext();
    const transversalInputProps = {
        errors: errors,
        i18labelBase: 'user.company',
    };

    return (
        <form onSubmit={onSubmit} noValidate>
            <InternalGrid
                displayType="grid"
                gridGap="md"
                alignItems="stretch"
                justifyItems="stretch"
                gridTemplateColumns="1fr"
            >
                {billingAddressPatterns
                    .filter((elem) => {
                        const { name } = elem;

                        return (
                            name === 'addressLine1' || name === 'addressLine2'
                        );
                    })
                    .map((elem) => {
                        const { name, type, pattern } = elem;

                        return (
                            <FormControlWrapper
                                key={name}
                                inputName={name}
                                isRequired={name === 'addressLine1'}
                                marginBottom={
                                    IsMobile() && name === 'addressLine2'
                                        ? 'sm'
                                        : null
                                }
                                {...transversalInputProps}
                            >
                                <DaInput
                                    onChange={(e) =>
                                        setValue(name, e.target.value)
                                    }
                                    name={name}
                                    id={name}
                                    type={type}
                                    defaultValue={defaultValues[name]}
                                    inputRef={register({
                                        required:
                                            name === 'addressLine1'
                                                ? t('form.error.required')
                                                : null,
                                        pattern: {
                                            value: pattern,
                                            message: t(`form.error.${name}`),
                                        },
                                    })}
                                    blockWidth="lg"
                                />
                            </FormControlWrapper>
                        );
                    })}
            </InternalGrid>

            <Box marginTop="sm">
                <InternalGrid
                    displayType="grid"
                    gridGap="md"
                    alignItems="stretch"
                    justifyItems="stretch"
                    gridTemplateColumns="1fr"
                >
                    {billingAddressPatterns
                        .filter((elem) => {
                            const { name } = elem;

                            return name === 'city' || name === 'zipCode';
                        })
                        .map((elem) => {
                            const { name, type, pattern } = elem;

                            return (
                                <FormControlWrapper
                                    key={name}
                                    inputName={name}
                                    isRequired={true}
                                    {...transversalInputProps}
                                >
                                    <DaInput
                                        onChange={(e) =>
                                            setValue(name, e.target.value)
                                        }
                                        name={name}
                                        id={name}
                                        type={type}
                                        defaultValue={defaultValues[name]}
                                        inputRef={register({
                                            required: t('form.error.required'),
                                            pattern: {
                                                value: pattern,
                                                message: t(
                                                    `form.error.${name}`,
                                                ),
                                            },
                                        })}
                                        blockWidth="md"
                                    />
                                </FormControlWrapper>
                            );
                        })}
                </InternalGrid>
            </Box>

            <InternalGrid
                displayType="grid"
                gridGap="md"
                alignItems="stretch"
                justifyItems="stretch"
                gridTemplateColumns={IsMobile() ? '1fr' : '1fr 1fr'}
            >
                {billingAddressPatterns
                    .filter((elem) => {
                        const { name } = elem;

                        return name === 'region';
                    })
                    .map((elem) => {
                        const { name, type, pattern } = elem;

                        return (
                            <FormControlWrapper
                                key={name}
                                inputName={name}
                                marginTop={IsMobile() ? null : 'md'}
                                {...transversalInputProps}
                            >
                                <DaInput
                                    onChange={(e) =>
                                        setValue(name, e.target.value)
                                    }
                                    name={name}
                                    id={name}
                                    type={type}
                                    defaultValue={defaultValues[name]}
                                    inputRef={register({
                                        pattern: {
                                            value: pattern,
                                            message: t(`form.error.${name}`),
                                        },
                                    })}
                                    blockWidth="md"
                                />
                            </FormControlWrapper>
                        );
                    })}

                <FormControlWrapper
                    hasStaticWidth={IsMobile()}
                    inputName="country"
                    isRequired={true}
                    marginTop={IsMobile() ? 'sm' : 'md'}
                    {...transversalInputProps}
                >
                    <DaSelect
                        hasStaticWidth={IsMobile()}
                        blockWidth="sm"
                        name="country"
                        id="country"
                        inputRef={register}
                        options={countryOptions(t)}
                        onChange={(e) => setValue('country', e.target.value)}
                        defaultValue={defaultValues.country}
                    />
                </FormControlWrapper>
            </InternalGrid>

            {children}
        </form>
    );
};

UserCompanyBillingAddressForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    defaultValues: PropTypes.object.isRequired,
};

export default withTranslation(
    'common',
    'countries',
)(UserCompanyBillingAddressForm);
